import { useCallback, useEffect, useMemo, useState } from "react";
import BaseWrapper from "../../../components/ui/BaseWrapper";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  deleteWorkOrder,
  fetchUserWorkOrders,
  fetchWorkOrderStatusCounts,
} from "../../../services/api/workOrder";
import dayjs from "dayjs";
import WorkOrderStatus from "../../../components/WorkOrderStatus";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import FilterChip from "../../../components/FilterChip";
import {
  debounce,
  displayFormattedTotalTimelog,
  formatPhoneNumber,
  navigateWithReload,
} from "../../../utils/helpers";
import BaseLoader from "../../../components/ui/BaseLoader";
import BaseTooltip from "../../../components/ui/BaseTooltip";

import { useDispatch, useSelector } from "react-redux";
import { showToast } from "../../../store/toast/toastSlice";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getTimeZoneAbbreviation } from "../../../utils/getTimeZoneAbbreviation";
import { fullName, joinNonEmpty } from "../../../utils/displayValue";
import { hasSchedule, workOrderLocalDateTime } from "../../../utils/workOrderSchedule";
import BaseDataTableV2 from "../../../components/ui/BaseDataTableV2";
import BaseDropdownV2 from "../../../components/ui/BaseDropdownV2";
import BaseTextField from "../../../components/ui/BaseTextField";
import { setClientFilters } from "../../../store/filters/clientFiltersSlice";
import WorkOrderFilters from "./WorkOrderFilters";
import TableColumnSettings from "../../../components/ui/TableColumnSettings";
import useTableColumnSettings from "../../../hooks/useTableColumnSettings";
import {
  fetchAllSubCategories,
  fetchAllVendors,
  fetchCategories,
} from "../../../services/api/category";
import {
  CanceledByType,
  CANCELLATION_REASON_LABELS,
  CurrencySymbol,
  ModuleType,
  PaymentType,
  UserType,
  WorkOrderStatusType,
} from "../../../utils/enum";
import { fetchCompanyUsers } from "../../../services/api/company";
import useAuth from "../../../hooks/useAuth";
import { fetchAllClients } from "../../../services/api/client";
import { fetchAllProjects } from "../../../services/api/project";
import GenerateEngineerInvoiceModal from "../../Invoices/components/GenerateEngineerInvoiceModal";
import { Badge, Menu, MenuItem } from "@mui/material";
import OpenableLink from "../../../components/ui/OpenableLink";
import UserProfileLink from "../../../components/ui/UserProfileLink";
import { fetchTeams } from "../../../services/api/teams";

dayjs.extend(utc);
dayjs.extend(timezone);

// Columns that must stay visible and pinned to the front of the table.
// The sticky-column styling and row navigation depend on the "id" column.
const LOCKED_COLUMN_FIELDS = ["id"];

const ISSUE_STATUSES = ["INCOMPLETE", "DISPUTED"];

const WorkOrderTable = ({
  setSelectedWorkOrderIds,
  selectedWorkOrderIds,
  setSelectedWorkOrders,
  setIsGenerateInvoiceModalOpen,
  isGenerateInvoiceModalOpen,
  setSelectedEngineer,
  selectedEngineer,
  setSelectedStatus,
  setSelectedCurrency,
  selectedCurrency,
}) => {
  const navigate = useNavigate();
  const { auth, getPermissionForModule } = useAuth();
  const { hasDeleteAccess, hasUpdateAccess } = getPermissionForModule(ModuleType.WORK_ORDERS);
  const companyId = auth?.user?.company?.id;
  const columnStorageKey = `workOrderListColumnSettings:${auth?.user?.id || "guest"}`;
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const clientFilters = useSelector((s) => s.clientFilters.workOrders);
  const { page, pageSize } = clientFilters.pagination;
  const defaultFilterParam = searchParams.get("filter") || "";
  const defaultFilterArray = defaultFilterParam.split(",").filter(Boolean);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [filtersChanged, setFiltersChanged] = useState(false);

  const [filters, setFilters] = useState(() => {
    if (defaultFilterArray.length > 0) {
      return { status: defaultFilterArray };
    } else {
      return { status: ["PUBLISHED"] };
    }
  });

  const [workOrders, setWorkOrders] = useState([]);
  const search = clientFilters.search;
  const [localSearch, setLocalSearch] = useState(search || "");
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [pinnedColumns, setPinnedColumns] = useState({
    left: ["id"],
    right: [],
  });
  const [paginationModel, setPaginationModel] = useState({
    page,
    pageSize,
  });

  const [queryParams, setQueryParams] = useState({
    page: page + 1,
    limit: pageSize,
    status: filters.status ? [filters.status] : [],
    search: "",
    sortBy: "orderStartDate,orderStartTime",
    sortOrder: "asc",
  });
  const filtersPayload = useMemo(
    () => ({
      categoryId: clientFilters.categoryId || undefined,
      vendorId: clientFilters.vendorId || undefined,
      subCategoryIds: clientFilters.subCategoryIds || undefined,
      teamIds: clientFilters.teamIds || undefined,
      clientIds: clientFilters.clientIds || undefined,
      minRate: clientFilters.minRate || undefined,
      maxRate: clientFilters.maxRate || undefined,
      minRequests: clientFilters.minRequests !== "" ? clientFilters.minRequests : undefined,
      maxRequests: clientFilters.maxRequests !== "" ? clientFilters.maxRequests : undefined,
      paymentType: clientFilters.paymentType || undefined,
      startDate: clientFilters.startDate || undefined,
      endDate: clientFilters.endDate || undefined,
      createStartDate: clientFilters.createStartDate || undefined,
      createEndDate: clientFilters.createEndDate || undefined,
      country: clientFilters.country.label || undefined,
      city: clientFilters.city?.label || undefined,
      providerId: clientFilters.provider.value || undefined,
      teamId: clientFilters.teamId || undefined,
      clientId: clientFilters.clientId || undefined,
      projectId: clientFilters.projectId || undefined,
      currency: clientFilters.currency || "",
      engineerCurrency: clientFilters.engineerCurrency || "",
      createdById: clientFilters.createdById || undefined,
      id: clientFilters.id || undefined,
      canceledBy: clientFilters.canceledBy || undefined,
      cancellationReason: clientFilters.cancellationReason || undefined,
    }),
    [clientFilters],
  );
  const columns = [
    {
      pinLeft: true,
      field: "id",
      headerName: "ID",
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <p
          onContextMenu={(e) => handleContextMenu(e, params)}
          className={`table-font-size font-normal text-primary cursor-pointer`}
          onClick={() => handleNavigate(params.value)}
        >
          {params.row?.referenceId}
        </p>
      ),
    },
    {
      field: "incidentId",
      headerName: "Incident ID",
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <p
          onContextMenu={(e) => handleContextMenu(e, params)}
          className={`table-font-size truncate max-w-40 overflow-hidden whitespace-nowrap cursor-default`}
        >
          {params.value}
        </p>
      ),
    },
    {
      field: "_count",
      width: 200,
      headerName: "Messages",
      sortable: false,
      renderCell: (params) => {
        const total = params?.row?.messageCount ?? 0;
        const unread = params?.row?.unreadMessageCount ?? 0;

        return (
          <div
            onContextMenu={(e) => handleContextMenu(e, params)}
            onClick={() => navigate(`/workOrder/messages/${params.row.id}`)}
            style={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <span className="text-primary">{total} Msgs </span>
            {unread > 0 && (
              <>
                <span></span>
                <Badge badgeContent={unread} color="secondary" sx={{ mr: 1 }}>
                  <span style={{ width: 8, height: 1 }} />
                </Badge>
              </>
            )}
          </div>
        );
      },
    },
    {
      field: "client",
      width: 200,
      headerName: "Client",
      sortable: false,
      renderCell: (params) => (
        <p
          onContextMenu={(e) => handleContextMenu(e, params)}
          className={`cursor-pointer `}
          // onClick={() =>
          //   navigate(`/workOrders/clients/edit/${params?.row?.client?.id}`)
          // }
        >
          {params?.row?.client && params?.row?.client?.clientName}
        </p>
      ),
    },
    {
      field: "project",
      width: 200,
      headerName: "Project",
      sortable: false,
      renderCell: (params) => (
        <p
          onContextMenu={(e) => handleContextMenu(e, params)}
          className={`cursor-pointer `}
          // onClick={() =>
          //   navigate(`/workOrders/projects/edit/${params?.row?.project?.id}`)
          // }
        >
          {params?.row?.project && params?.row?.project?.title}
        </p>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      // Room for "Submitted -> Counter Received" without clipping the sub-status chip.
      width: 260,
      sortable: false,
      renderCell: (params) => {
        return (
          <div className="flex items-center" onContextMenu={(e) => handleContextMenu(e, params)}>
            <WorkOrderStatus
              status={params.value}
              className="!py-[1.8px] !min-w-[70px] !w-auto !px-2 !text-[11px] whitespace-nowrap"
            />

            {params.value !== "CANCELLED" &&
              params.row?.subStatus &&
              !(
                params.row.subStatus === WorkOrderStatusType.PO_PENDING &&
                params.row?.purchaseOrderNumber?.trim()?.length > 1
              ) && (
                <>
                  <i className="fa-regular fa-arrow-right-long mx-2 text-primary"></i>

                  <div className="ml-1">
                    <WorkOrderStatus
                      status={params.row.subStatus}
                      className="!py-[1.5px] !min-w-[80px] !w-auto !px-2 whitespace-nowrap !text-[9px]"
                    />
                  </div>
                </>
              )}

            {params.value === "CANCELLED" && params.row?.customerStatus && (
              <>
                <i className="fa-regular fa-arrow-right-long mx-2 text-primary"></i>

                <div className="ml-1">
                  <WorkOrderStatus
                    status={
                      params.row.subStatus === WorkOrderStatusType.PO_PENDING
                        ? params.row.subStatus
                        : params.row?.customerStatus
                    }
                    className="!py-[1.5px] !min-w-[80px] !w-auto !px-2 whitespace-nowrap !text-[9px]"
                  />
                </div>
              </>
            )}
          </div>
        );
      },
    },
    {
      field: "title",
      headerName: "Title",
      width: 300,
      sortable: false,
      renderCell: (params) => (
        <p
          onContextMenu={(e) => handleContextMenu(e, params)} // Right-click triggers context menu
          className="table-font-size truncate max-w-64 overflow-hidden whitespace-nowrap text-primary cursor-pointer"
          onClick={() => handleNavigate(params.row?.id)}
        >
          {params.value}
        </p>
      ),
    },
    {
      field: "engineer",
      width: 240,
      headerName: "Engineer",
      sortable: false,
      renderCell: (params) => {
        const engineer = params?.value;
        if (!engineer) return null;
        const phoneNumber = formatPhoneNumber(engineer.phoneNumber);
        return (
          <div
            onContextMenu={(e) => handleContextMenu(e, params)}
            className="flex flex-col justify-center leading-tight whitespace-nowrap font-light"
          >
            <span className="flex items-center gap-1">
              <OpenableLink
                to={`/user/details/profile/${engineer?.id}?direct=true`}
                className="text-primary cursor-pointer"
              >
                {engineer.displayName || `${engineer.firstName} ${engineer?.lastName ?? ""}`}
              </OpenableLink>
              {(engineer?.displayId || engineer?.id) && (
                <span className="text-xs text-gray-500">
                  ID: {engineer?.displayId || engineer.id}
                </span>
              )}
            </span>
            {phoneNumber && (
              <span className="text-xs text-gray-500">{formatPhoneNumber(phoneNumber)}</span>
            )}
          </div>
        );
      },
    },
    {
      field: "orderStartDate",
      headerName: "Schedule",
      width: 200,
      sortable: true,
      renderCell: (params) => (
        <div>
          <p className={`table-font-size`} onContextMenu={(e) => handleContextMenu(e, params)}>
            {/* A draft may have no schedule yet — formatting null here would
                print the literal string "Invalid Date". */}
            {hasSchedule(params?.row?.orderStartDate, params?.row?.orderStartTime) ? (
              <>
                {dayjs(params?.row?.orderStartDate).format("MM/DD/YYYY")}

                <span> at </span>

                {dayjs.utc(params?.row?.orderStartTime).format("hh:mm A")}
                <span> ({getTimeZoneAbbreviation(params?.row?.timeZone) || ""})</span>
              </>
            ) : (
              "-"
            )}
          </p>
        </div>
      ),
    },
    {
      field: "localTime",
      headerName: "Local Time",
      width: 200,
      sortable: false,
      renderCell: (params) => {
        const userTimeZone = dayjs.tz.guess();

        // Null-safe: a draft may have no schedule yet, and dayjs.tz() throws on
        // the "Invalid Date" string rather than returning an invalid dayjs.
        const convertedDateTime = workOrderLocalDateTime(
          params?.row?.orderStartDate,
          params?.row?.orderStartTime,
          params?.row?.timeZone,
        );
        return (
          <div onContextMenu={(e) => handleContextMenu(e, params)}>
            <p className="table-font-size">
              {convertedDateTime ? (
                <>
                  {convertedDateTime.format("MM/DD/YYYY")}
                  <span> at </span>
                  {convertedDateTime.format("hh:mm A")}
                  <span> ({getTimeZoneAbbreviation(userTimeZone)})</span>
                </>
              ) : (
                "-"
              )}
            </p>
          </div>
        );
      },
    },
    {
      field: "address",
      headerName: "Address",
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <div className="cursor-default" onContextMenu={(e) => handleContextMenu(e, params)}>
          <BaseTooltip
            position="top"
            interactive={true}
            content={
              <div className="relative pb-2">
                <p className="text-white">
                  {joinNonEmpty([params?.value?.addressLine1, params?.value?.country])}
                </p>
                {/* <i
                  className="fa-solid fa-diamond-turn-right text-secondary text-xl top-9 right-0 absolute cursor-pointer"
                  onClick={() =>
                    openInGoogleMaps(
                      params?.value?.latitude,
                      params?.value?.longitude
                    )
                  }
                ></i> */}
              </div>
            }
          >
            <p className="table-font-size truncate max-w-72 overflow-hidden whitespace-nowrap">
              {joinNonEmpty([params?.value?.addressLine1, params?.value?.country])}
            </p>
          </BaseTooltip>
        </div>
      ),
    },
    {
      field: "notes",
      width: 200,
      headerName: "Notes",
      sortable: false,
      renderCell: (params) => (
        <div className="cursor-default">
          <BaseTooltip
            position="top"
            interactive={true}
            content={<p className="text-white">{params.value}</p>}
          >
            <p className="table-font-size truncate max-w-72 overflow-hidden whitespace-nowrap">
              {params.value}
            </p>
          </BaseTooltip>
        </div>
      ),
    },
    {
      field: "timeLogs",
      width: 200,
      headerName: "Check In",
      sortable: false,
      renderCell: (params) => (
        <p className={``} onContextMenu={(e) => handleContextMenu(e, params)}>
          {params?.value[0]?.checkInTime &&
            `${dayjs(params?.value[0]?.checkInTime).tz(params?.row?.timeZone).format("h:mm A")}`}
        </p>
      ),
    },
    {
      field: "timeLogs1",
      width: 200,
      headerName: "Check Out",
      sortable: false,
      renderCell: (params) => (
        <p className={``} onContextMenu={(e) => handleContextMenu(e, params)}>
          {params?.row?.timeLogs[params.row?.timeLogs.length - 1]?.checkOutTime &&
            `${dayjs(params?.row?.timeLogs[params.row?.timeLogs.length - 1]?.checkOutTime)
              .tz(params?.row?.timeZone)
              .format("h:mm A")}`}
        </p>
      ),
    },
    {
      field: "engineerHoursWorked",
      width: 200,
      headerName: "Hours Logged",
      sortable: false,
      renderCell: (params) => (
        // <p className={``}>{params?.value?.toFixed(2) || 0.0} </p>
        <p className={``} onContextMenu={(e) => handleContextMenu(e, params)}>
          {params?.value !== undefined && params?.value !== null && params?.value !== 0
            ? displayFormattedTotalTimelog(params.value)
            : ""}
        </p>
      ),
    },
    {
      field: "paymentType",
      headerName: "Customer Rate",
      width: 250,
      sortable: false,
      renderCell: (params) => {
        return (
          <div
            onContextMenu={(e) => handleContextMenu(e, params)}
            className="cursor-pointer"
            // onClick={() => navigate(`/workOrder/payment/${params.row?.id}`)}
          >
            {params.row?.paymentType === "FTH" && (
              <p className="table-font-size">
                <span className="text-primary">{PaymentType.FTH}</span> @{" "}
                {CurrencySymbol[params.row?.currency] || ""} {params.row?.fthRate?.toFixed(2)} &
                Additional hrs @ {CurrencySymbol[params.row?.currency] || ""}{" "}
                {params.row?.hourlyRate?.toFixed(2)}
              </p>
            )}
            {params.row?.paymentType === "CUSTOM_HOURS" && (
              <p className="table-font-size">
                <span className="text-primary">
                  Fixed Hours (
                  {/* Blank rather than "undefined hour" when the draft has not
                      set the hours yet. */}
                  {params.row?.customHours
                    ? `${params.row.customHours} ${params.row.customHours > 1 ? "hours" : "hour"}`
                    : ""}
                  )
                </span>{" "}
                @ {CurrencySymbol[params.row?.currency] || ""}{" "}
                {params.row?.customHoursRate?.toFixed(2)} & Additional hrs @{" "}
                {CurrencySymbol[params.row?.currency] || ""} {params.row?.hourlyRate?.toFixed(2)}
              </p>
            )}
            {params.row?.paymentType === "HOURLY" && (
              <p className="table-font-size">
                <span className="text-primary">{PaymentType.HOURLY}</span> @{" "}
                {CurrencySymbol[params.row?.currency] || ""} {params.row?.hourlyRate?.toFixed(2)}{" "}
                (min {params.row.minimumHours} hrs)
              </p>
            )}
            {params.row?.paymentType === "HALF_DAY" && (
              <p className="table-font-size">
                <span className="text-primary">{PaymentType.HALF_DAY}</span> @{" "}
                {CurrencySymbol[params.row?.currency] || ""} {params.row?.halfDayRate?.toFixed(2)} &
                Additional hrs @ {CurrencySymbol[params.row?.currency] || ""}{" "}
                {params.row?.hourlyRate?.toFixed(2)}
              </p>
            )}
            {params.row?.paymentType === "FULL_DAY" && (
              <p className="table-font-size">
                <span className="text-primary">{PaymentType.FULL_DAY}</span> @{" "}
                {CurrencySymbol[params.row?.currency] || ""} {params?.row?.fullDayRate?.toFixed(2)}{" "}
                & Additional hrs @ {CurrencySymbol[params.row?.currency] || ""}{" "}
                {params.row?.hourlyRate?.toFixed(2)}
              </p>
            )}
            {params.row?.paymentType === "FIXED" && (
              <p className="table-font-size">
                <span className="text-primary">{PaymentType.FIXED}</span> @{" "}
                {CurrencySymbol[params.row?.currency] || ""} {params?.row?.fixedAmount?.toFixed(2)}
              </p>
            )}
          </div>
        );
      },
    },
    {
      field: "engineerRates",
      headerName: "Engineer Rate",
      width: 320,
      sortable: false,
      renderCell: (params) => {
        return (
          <>
            {
              <div className="cursor-pointer" onContextMenu={(e) => handleContextMenu(e, params)}>
                {params.row?.engineerPaymentType === "FTH" && (
                  <p className="table-font-size">
                    <span className="text-primary">{PaymentType.FTH}</span>

                    {getEngineerValue(params, "fthRate") != null && (
                      <>
                        {" "}
                        @{" "}
                        {CurrencySymbol[params.row?.engineerCurrency] ||
                          CurrencySymbol[params.row?.engineer?.offer.engineerCurrency] ||
                          ""}{" "}
                        {getEngineerValue(params, "fthRate").toFixed(2)}
                      </>
                    )}

                    {getEngineerValue(params, "hourlyRate") != null && (
                      <>
                        {" "}
                        & Additional hrs @{" "}
                        {CurrencySymbol[params.row?.engineerCurrency] ||
                          CurrencySymbol[params.row?.engineer?.offer.engineerCurrency] ||
                          ""}{" "}
                        {getEngineerValue(params, "hourlyRate").toFixed(2)}
                      </>
                    )}
                  </p>
                )}

                {params.row?.engineerPaymentType === "HOURLY" && (
                  <p className="table-font-size">
                    <span className="text-primary">{PaymentType.HOURLY}</span>

                    {getEngineerValue(params, "hourlyRate") != null && (
                      <>
                        {" "}
                        @{" "}
                        {CurrencySymbol[params.row?.engineerCurrency] ||
                          CurrencySymbol[params.row?.engineer?.offer.engineerCurrency] ||
                          ""}{" "}
                        {getEngineerValue(params, "hourlyRate").toFixed(2)}
                      </>
                    )}

                    {getEngineerValue(params, "minimumHours") != null && (
                      <> (min {getEngineerValue(params, "minimumHours")} hrs)</>
                    )}
                  </p>
                )}

                {params.row?.engineerPaymentType === "HALF_DAY" && (
                  <p className="table-font-size">
                    <span className="text-primary">{PaymentType.HALF_DAY}</span>
                    {getEngineerValue(params, "halfDayRate") != null && (
                      <>
                        {" "}
                        @{" "}
                        {CurrencySymbol[params.row?.engineerCurrency] ||
                          CurrencySymbol[params.row?.engineer?.offer.engineerCurrency] ||
                          ""}{" "}
                        {getEngineerValue(params, "halfDayRate").toFixed(2)}
                      </>
                    )}

                    {getEngineerValue(params, "hourlyRate") != null && (
                      <>
                        {" "}
                        & Additional hrs @{" "}
                        {CurrencySymbol[params.row?.engineerCurrency] ||
                          CurrencySymbol[params.row?.engineer?.offer.engineerCurrency] ||
                          ""}{" "}
                        {getEngineerValue(params, "hourlyRate").toFixed(2)}
                      </>
                    )}
                  </p>
                )}
                {params.row?.engineerPaymentType === "FULL_DAY" && (
                  <p className="table-font-size">
                    <span className="text-primary">{PaymentType.FULL_DAY}</span>
                    {getEngineerValue(params, "fullDayRate") != null && (
                      <>
                        {" "}
                        @{" "}
                        {CurrencySymbol[params.row?.engineerCurrency] ||
                          CurrencySymbol[params.row?.engineer?.offer.engineerCurrency] ||
                          ""}{" "}
                        {getEngineerValue(params, "fullDayRate").toFixed(2)}
                      </>
                    )}

                    {getEngineerValue(params, "hourlyRate") != null && (
                      <>
                        {" "}
                        & Additional hrs @{" "}
                        {CurrencySymbol[params.row?.engineerCurrency] ||
                          CurrencySymbol[params.row?.engineer?.offer.engineerCurrency] ||
                          ""}{" "}
                        {getEngineerValue(params, "hourlyRate").toFixed(2)}
                      </>
                    )}
                  </p>
                )}
                {params.row?.engineerPaymentType === "FIXED" && (
                  <p className="table-font-size">
                    <span className="text-primary">{PaymentType.FIXED}</span>
                    {getEngineerValue(params, "fixedAmount") != null && (
                      <>
                        {" "}
                        @{" "}
                        {CurrencySymbol[params.row?.engineerCurrency] ||
                          CurrencySymbol[params.row?.engineer?.offer.engineerCurrency] ||
                          ""}{" "}
                        {getEngineerValue(params, "fixedAmount").toFixed(2)}
                      </>
                    )}
                  </p>
                )}
              </div>
            }
          </>
        );
      },
    },
    {
      field: "createdBy",
      width: 200,
      headerName: "Created By",
      sortable: false,
      renderCell: (params) => (
        <div onContextMenu={(e) => handleContextMenu(e, params)}>
          <UserProfileLink userId={params?.value?.id}>{fullName(params?.value)}</UserProfileLink>
        </div>
      ),
    },
    {
      field: "requested",
      headerName: "Requested",
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <p
          onContextMenu={(e) => handleContextMenu(e, params)}
          className={`table-font-size text-primary cursor-pointer`}
          onClick={() => navigate(`/workOrder/providers/${params.row.id}?tab=pending`)}
        >
          {params?.row?.requested || 0}
        </p>
      ),
    },
    {
      field: "countered",
      headerName: "Countered",
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <p
          onContextMenu={(e) => handleContextMenu(e, params)}
          className={`table-font-size text-primary cursor-pointer`}
          onClick={() => navigate(`/workOrder/providers/${params.row.id}?tab=pending`)}
        >
          {params?.row?.countered || 0}
        </p>
      ),
    },
    {
      field: "routed",
      headerName: "Routed",
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <p
          onContextMenu={(e) => handleContextMenu(e, params)}
          className={`table-font-size text-primary cursor-pointer`}
          onClick={() => navigate(`/workOrder/providers/${params.row.id}?tab=pending`)}
        >
          {params?.row?.routed || 0}
        </p>
      ),
    },
    {
      field: "purchaseOrders",
      headerName: "Purchase Order",
      width: 300,
      sortable: false,
      renderCell: (params) => {
        const { purchaseOrders, purchaseOrderNumber } = params.row;

        if (purchaseOrderNumber && (!purchaseOrders || purchaseOrders.length === 0)) {
          return <p className="cursor-default">{purchaseOrderNumber}</p>;
        }

        if (purchaseOrders?.length) {
          return (
            <div
              className="truncate w-full text-ellipsis whitespace-nowrap"
              title={purchaseOrders.map((po) => po.number).join(", ")}
            >
              <p className="truncate w-full text-ellipsis whitespace-nowrap">
                {purchaseOrders.map((po, index) => (
                  <span key={po.id}>
                    {po.attachment?.url ? (
                      <a
                        href={po.attachment.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary cursor-pointer hover:underline"
                      >
                        {po.number}
                      </a>
                    ) : (
                      <span className="cursor-default">{po.number}</span>
                    )}
                    {index !== purchaseOrders.length - 1 && ", "}
                  </span>
                ))}
              </p>
            </div>
          );
        }

        return <span>-</span>;
      },
    },
    {
      field: "team",
      width: 200,
      headerName: "Team",
      sortable: false,
      renderCell: (params) => (
        <p onContextMenu={(e) => handleContextMenu(e, params)} className={`cursor-pointer `}>
          {params?.row?.client && params?.row?.team?.name}
        </p>
      ),
    },
    {
      field: "workOrderManager",
      headerName: "Manager",
      width: 200,
      sortable: false,
      renderCell: (params) =>
        params?.value && (
          <div onContextMenu={(e) => handleContextMenu(e, params)}>
            <UserProfileLink userId={params?.value?.id}>{fullName(params?.value)}</UserProfileLink>
          </div>
        ),
    },
    {
      field: "locationName",
      width: 200,
      headerName: "Location Name",
      sortable: false,
      renderCell: (params) => (
        <p
          className={`table-font-size truncate max-w-72 overflow-hidden whitespace-nowrap`}
          onContextMenu={(e) => handleContextMenu(e, params)}
        >
          {params?.row?.address?.displayName}
        </p>
      ),
    },
    {
      field: "category",
      width: 200,
      headerName: "Type of Work",
      sortable: false,
      renderCell: (params) => (
        <p
          className="table-font-size"
          onContextMenu={(e) => handleContextMenu(e, params)}
        >{`${params?.value?.category || "not specified"}`}</p>
      ),
    },
    {
      field: "cancelledWorkOrder",
      width: 150,
      headerName: "Cancelled By",
      sortable: false,
      renderCell: (params) => {
        const cancellation = params?.value?.[0];
        if (!cancellation?.canceledBy) return null;
        const label = cancellation.canceledBy === CanceledByType.CLIENT ? "Client" : "Tech";
        const reasonKey = cancellation.cancellationReason;
        const reasonText = reasonKey ? CANCELLATION_REASON_LABELS[reasonKey] || reasonKey : null;
        const customReason = cancellation.reason;
        const tooltipContent = (
          <div>
            {reasonText && <p className="text-white">{reasonText}</p>}
            {customReason && <p className="text-white text-xs mt-1">{customReason}</p>}
          </div>
        );
        return (
          <div onContextMenu={(e) => handleContextMenu(e, params)}>
            {reasonText ? (
              <BaseTooltip position="top" content={tooltipContent}>
                <span className="table-font-size cursor-default">{label}</span>
              </BaseTooltip>
            ) : (
              <span className="table-font-size">{label}</span>
            )}
          </div>
        );
      },
    },
    {
      field: "createdAt",
      width: 200,
      headerName: "Created",
      sortable: false,
      renderCell: (params) => (
        <p className={``} onContextMenu={(e) => handleContextMenu(e, params)}>
          {dayjs(params?.value).format("MM/DD/YYYY")} at {dayjs(params?.value).format("hh:mm A")}
        </p>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const menuOptions = [
          // A draft is still being composed, so it is the one state worth editing
          // straight from the list — everything else is managed from its detail
          // page, where the status-specific actions live.
          ...(hasUpdateAccess && params?.row?.status === WorkOrderStatusType.DRAFT
            ? [{ label: "Edit", value: "EDIT" }]
            : []),
          ...(hasDeleteAccess ? [{ label: "Delete", value: "DELETE" }] : []),
        ];

        return (
          <BaseDropdownV2
            menuOptions={menuOptions}
            triggerElement={<i class="fa-solid fa-ellipsis cursor-pointer ml-4 text-base"></i>}
            onItemClick={(value) => handleItemClick(value, params?.row)}
          />
        );
      },
      pinRight: true,
    },
  ];

  const columnSettings = useTableColumnSettings({
    columns,
    storageKey: columnStorageKey,
    lockedFields: LOCKED_COLUMN_FIELDS,
  });

  // Only the visible columns' data is fetched. "In Flight" is filtered in the
  // browser off orderStartDate, so that column is requested while that filter
  // is on even when it is hidden.
  const visibleColumns = useMemo(() => {
    const fields = columnSettings.visibleFields;
    const needsSchedule =
      filters.status?.includes("INFLIGHT") && !fields.includes("orderStartDate");
    return (needsSchedule ? [...fields, "orderStartDate"] : fields).join(",");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnSettings.visibleFields, filters.status]);

  const {
    data: workOrdersData,
    isLoading: isUserWorkOrderLoading,
    error: isWorkOrderError,
    refetch: refetchUserWorkOrders,
  } = useQuery({
    queryKey: ["userWorkOrders", queryParams, filtersPayload, visibleColumns],
    queryFn: () =>
      fetchUserWorkOrders({
        ...queryParams,
        ...filtersPayload,
        sortBy: queryParams.sortBy,
        sortOrder: queryParams.sortOrder,
        columns: visibleColumns,
      }),
  });

  const { data: statusCountData, refetch: refetchStatusCounts } = useQuery({
    queryKey: ["workOrderStatusCounts", filtersPayload, clientFilters.search, "workboard"],
    queryFn: () =>
      fetchWorkOrderStatusCounts({
        ...filtersPayload,
        search: clientFilters.search,
      }),
  });

  const {
    isLoading: isLoginLoading,
    status,
    error,
    mutateAsync: deleteWorkOrderMutation,
  } = useMutation({
    mutationFn: deleteWorkOrder,
  });

  const { data: categoriesData } = useQuery({
    queryKey: ["categories"],
    queryFn: fetchCategories,
    enabled: isFilterDrawerOpen,
  });

  const { data: vendorsData } = useQuery({
    queryKey: ["vendors"],
    queryFn: fetchAllVendors,
    enabled: isFilterDrawerOpen,
  });

  const { data: subCategoriesData } = useQuery({
    queryKey: ["subCategories"],
    queryFn: fetchAllSubCategories,
    enabled: isFilterDrawerOpen,
  });

  const companyParams = { id: companyId, filter: true };

  const {
    data: companyUsers,
    isPending: isCompanyUsersApiLoading,
    error: isCompanyUsersApiError,
  } = useQuery({
    queryKey: ["companyUsers", companyParams.id, companyParams.filter],
    queryFn: () => fetchCompanyUsers(companyParams),
    enabled: !!companyParams.id && isFilterDrawerOpen,
  });

  const { isPending: isClientsLoading, data: clientsData } = useQuery({
    queryKey: ["clients", companyId, "entities"],
    queryFn: () =>
      fetchAllClients({
        companyId: companyId?.toString(),
        isParentClient: "false",
      }),
    enabled: !!companyId && isFilterDrawerOpen,
  });

  const { isPending: isProjectsLoading, data: projectsData } = useQuery({
    queryKey: ["projects", companyId],
    queryFn: () =>
      fetchAllProjects({
        companyId: companyId?.toString(),
      }),
    enabled: !!companyId && isFilterDrawerOpen,
  });

  const { isPending: isTeamsLoading, data: teamsData } = useQuery({
    queryKey: ["teams", companyId],
    queryFn: () =>
      fetchTeams({
        companyId: companyId?.toString(),
      }),
    enabled: !!companyId && isFilterDrawerOpen,
  });

  const categories = categoriesData?.data || [];
  const vendors = vendorsData?.data || [];
  const subCategories = subCategoriesData?.data || [];
  const appliedFilters = useMemo(() => {
    const filters = [];
    if (clientFilters?.country?.value) {
      filters.push({
        prefix: "Country",
        value: clientFilters.country.label,
        key: "country",
      });
    }
    if (clientFilters?.city?.value) {
      filters.push({ prefix: "City", value: clientFilters.city.label, key: "city" });
    }

    if (clientFilters?.provider?.value) {
      filters.push({
        prefix: "Provider",
        value: clientFilters.provider.label,
        key: "provider",
      });
    }
    if (clientFilters?.teamId) {
      const teamName = teamsData?.data?.data?.find((t) => t.id === clientFilters.teamId)?.name;

      if (teamName)
        filters.push({
          prefix: "Team",
          value: teamName,
          key: "teamId",
        });
    }

    if (clientFilters?.clientIds?.length > 0) {
      const items = clientsData?.data?.data
        .filter((s) => clientFilters.clientIds.includes(s.id))
        .map((s) => ({ id: s.id, name: s.clientName }));

      items.forEach(({ id, name }) =>
        filters.push({
          prefix: "Client",
          value: name,
          key: "clientIds",
          id,
        }),
      );
    }
    if (clientFilters?.teamIds?.length > 0) {
      const items = teamsData?.data?.data
        .filter((s) => clientFilters.teamIds.includes(s.id))
        .map((s) => ({ id: s.id, name: s.name }));

      items.forEach(({ id, name }) =>
        filters.push({
          prefix: "Team",
          value: name,
          key: "teamIds",
          id,
        }),
      );
    }

    if (clientFilters?.projectId) {
      const projectName = projectsData?.data?.projects?.find(
        (c) => c.id === clientFilters.projectId,
      )?.title;
      if (projectName)
        filters.push({
          prefix: "Project",
          value: projectName,
          key: "projectId",
        });
    }
    if (clientFilters?.currency) {
      const currency = clientFilters.currency || "Currency";
      filters.push({
        prefix: "Currency",
        value: currency,
        key: "currency",
      });
    }
    if (clientFilters?.engineerCurrency) {
      const engineerCurrency = clientFilters.engineerCurrency || "EngineerCurrency";
      filters.push({
        prefix: "Engineer Currency",
        value: engineerCurrency,
        key: "engineerCurrency",
      });
    }
    if (clientFilters?.appliedFromDrawer && clientFilters?.status?.length > 0) {
      clientFilters.status.forEach((status) => {
        filters.push({
          prefix: "Status",
          // Display first-letter-capital, rest lowercase (e.g. APPROVED -> Approved).
          // Keep the raw enum in `id` so removal still matches.
          value: status.charAt(0).toUpperCase() + status.slice(1).toLowerCase(),
          key: "status",
          id: status,
        });
      });
    }

    if (clientFilters?.createdById) {
      const name =
        companyUsers?.data?.users?.find((c) => c.id === clientFilters.createdById)?.firstName +
        " " +
        companyUsers?.data?.users?.find((c) => c.id === clientFilters.createdById)?.lastName;
      if (name) filters.push({ prefix: "Created By", value: name, key: "createdById" });
    }

    if (clientFilters?.id) {
      const id = clientFilters.id || "ID";
      filters.push({
        prefix: "ID",
        value: id,
        key: "id",
      });
    }

    if (clientFilters?.categoryId) {
      const name = categories.find((c) => c.id === clientFilters.categoryId)?.category;
      if (name) filters.push({ prefix: "Technology Stack", value: name, key: "categoryId" });
    }

    if (clientFilters?.vendorId) {
      const name = vendors.find((v) => v.id === clientFilters.vendorId)?.name;
      if (name) filters.push({ prefix: "Category", value: name, key: "vendorId" });
    }

    if (clientFilters?.subCategoryIds?.length > 0) {
      const items = subCategories
        .filter((s) => clientFilters.subCategoryIds.includes(s.id))
        .map((s) => ({ id: s.id, name: s.subCategory }));

      items.forEach(({ id, name }) =>
        filters.push({
          prefix: "Skill",
          value: name,
          key: "subCategoryIds",
          id,
        }),
      );
    }

    if (clientFilters?.minRate || clientFilters?.maxRate) {
      const min = Number(clientFilters.minRate ?? 0);
      const max = Number(clientFilters.maxRate ?? 100);
      filters.push({
        prefix: "Rate",
        value: min === max ? `$${min}` : `$${min} - $${max}`,
        key: "rateRange",
      });
    }

    if (clientFilters?.startDate || clientFilters?.endDate) {
      filters.push({
        prefix: "Date",
        value: `${clientFilters.startDate || "Any"} - ${clientFilters.endDate || "Any"}`,
        key: "dateRange",
      });
    }

    if (clientFilters?.createStartDate || clientFilters?.createEndDate) {
      filters.push({
        prefix: "Date",
        value: `${clientFilters.createStartDate || "Any"} - ${clientFilters.createEndDate || "Any"}`,
        key: "createDateRange",
      });
    }

    if (clientFilters?.paymentType) {
      const label = PaymentType[clientFilters.paymentType] || clientFilters.paymentType;
      filters.push({
        prefix: "Payment",
        value: label,
        key: "paymentType",
      });
    }

    if (clientFilters?.minRequests !== "" || clientFilters?.maxRequests !== "") {
      const min = clientFilters.minRequests ?? 0;
      const max = clientFilters.maxRequests ?? 0;

      filters.push({
        prefix: "Requests",
        value: min === max ? `${min}` : `${min} - ${max}`,
        key: "requestsRange",
      });
    }

    if (clientFilters?.canceledBy) {
      const label = clientFilters.canceledBy === CanceledByType.CLIENT ? "Client" : "Tech";
      filters.push({ prefix: "Cancelled By", value: label, key: "canceledBy" });
    }

    if (clientFilters?.cancellationReason) {
      const label =
        CANCELLATION_REASON_LABELS[clientFilters.cancellationReason] ||
        clientFilters.cancellationReason;
      filters.push({ prefix: "Cancel Reason", value: label, key: "cancellationReason" });
    }

    return filters;
  }, [clientFilters, categories, vendors, subCategories, clientsData, teamsData]);
  const hasAppliedFilters = appliedFilters.length > 0;

  const statusCounts = statusCountData?.statusCounts || {};
  const totalWorkOrdersCount = statusCountData?.totalCount || 0;

  const statusArray = Array.isArray(filters.status)
    ? filters.status
    : filters.status
      ? [filters.status]
      : [];

  const rowCount = (() => {
    const firstStatus = Array.isArray(filters.status) ? filters.status[0] : filters.status;
    const statusArray = Array.isArray(filters.status) ? filters.status : [filters.status];
    const totalCount = workOrdersData?.data?.totalCount || 0;

    // Case 1: search is active OR firstStatus is empty → show total count
    if (search || firstStatus === "" || !firstStatus) {
      return totalCount;
    }

    // Case 2: client drawer filter applied
    if (clientFilters?.appliedFromDrawer) {
      if (statusArray.length === 1) {
        return statusCounts[firstStatus] || 0;
      } else {
        // multiple statuses → treat as all
        return totalCount;
      }
    }

    // Multiple statuses (e.g. the Issue chip) → the filtered total already covers them
    if (statusArray.length > 1) {
      return totalCount;
    }

    // Case 3: firstStatus is defined
    if (firstStatus) {
      if (firstStatus === "APPROVED") {
        return totalCount;
      } else {
        return statusCounts[firstStatus] || 0;
      }
    }

    // Fallback: show totalWorkOrdersCount
    return totalWorkOrdersCount;
  })();
  useEffect(() => {
    setLocalSearch(search || "");
  }, [search]);

  useEffect(() => {
    setQueryParams((prev) => ({
      ...prev,
      page: page + 1,
      status: clientFilters.status?.length ? clientFilters.status : [...filters.status],
      search: prev.search,
    }));

    applyFilter();
  }, [filters]);

  useEffect(() => {
    if (clientFilters.status && clientFilters.status.length > 0) {
      setFilters({ status: clientFilters.status });
    } else {
      setFilters({ status: ["PUBLISHED"] });
    }
  }, [clientFilters.status]);

  useEffect(() => {
    if (clientFilters.engineerCurrency) {
      setSelectedCurrency(clientFilters.engineerCurrency);
    }
    if (clientFilters.provider) {
      setSelectedEngineer({
        providerName: clientFilters.provider.label,
        providerId: clientFilters.provider.value,
      });
    }
    if (clientFilters.status && clientFilters.status.length > 0) {
      setSelectedStatus(clientFilters.status);
    }
  }, [clientFilters]);

  useEffect(() => {
    if (filtersChanged) {
      setFiltersChanged(false);
    }
  }, [filtersChanged]);

  useEffect(() => {
    setPaginationModel({ page, pageSize });
    setQueryParams((prev) => ({ ...prev, page: page + 1, limit: pageSize }));
  }, [page, pageSize]);

  const handleSortModelChange = (newSortModel) => {
    if (newSortModel.length > 0) {
      const sortDirection = newSortModel[0].sort;

      setQueryParams((prev) => ({
        ...prev,
        sortBy: "orderStartDate,orderStartTime",
        sortOrder: sortDirection,
      }));
    } else {
      setQueryParams((prev) => ({
        ...prev,
        sortBy: "orderStartDate,orderStartTime",
        sortOrder: "asc",
      }));
    }
  };

  const applyFilter = () => {
    const allWorkOrders = workOrdersData?.data?.workOrders || [];
    const statusToFilter = clientFilters.status?.length ? clientFilters.status : filters.status;
    if (!filters.status || filters.status.length === 0 || filters.status.includes("")) {
      setWorkOrders(allWorkOrders);
      return;
    }

    if (filters.status.includes("INFLIGHT")) {
      const filtered = filterInFlightWorkOrders();
      setWorkOrders(filtered);
      return;
    }

    // The server already filtered by status; only re-filter when the Status
    // column's data was actually requested.
    const filteredWorkOrders = allWorkOrders.filter(
      (wo) => !("status" in wo) || statusToFilter.includes(wo.status),
    );
    setWorkOrders(filteredWorkOrders);
  };

  const normalizeStatus = (input) => {
    if (Array.isArray(input)) return input;
    if (typeof input === "string") return [input];
    return [""];
  };

  const handleFilterClick = (status) => {
    let statusArray = normalizeStatus(status);

    setFilters({ status: statusArray });

    const queryString = statusArray.filter(Boolean).join(",");
    setSearchParams({ filter: queryString });

    dispatch(
      setClientFilters({
        module: "workOrders",
        filterName: "status",
        data: statusArray,
      }),
    );
    dispatch(
      setClientFilters({
        module: "workOrders",
        filterName: "appliedFromDrawer",
        data: false,
      }),
    );
  };

  const handleContextMenu = (event, item) => {
    event.preventDefault();
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
          }
        : null,
    );
    setSelectedWorkOrder(item);
  };

  const handleClose = () => {
    setContextMenu(null);
    setSelectedWorkOrder(null);
  };

  const handlePaginationChange = (newPaginationModel) => {
    setPaginationModel(newPaginationModel);
    setQueryParams((prev) => ({
      ...prev,
      page: newPaginationModel.page + 1,
      limit: newPaginationModel.pageSize,
    }));
    dispatch(
      setClientFilters({
        module: "workOrders",
        filterName: "pagination",
        data: {
          page: newPaginationModel.page,
          pageSize: newPaginationModel.pageSize,
        },
      }),
    );
  };

  const convertToLocalTime = (time, timeZone) => {
    if (!timeZone) return;

    const targetTime = dayjs(time).tz(timeZone, true);

    const localTime = targetTime.local().format("hh:mm A");

    return localTime;
  };

  const getEngineerValue = (params, field) => {
    const offer = params.row?.engineer?.offer;

    const offerFieldMap = {
      hourlyRate: offer?.hourlyRate,
      fullDayRate: offer?.fullDayRate,
      halfDayRate: offer?.halfDayRate,
      fthRate: offer?.fthRate,
      fixedAmount: offer?.fixedAmount,
      minimumHours: offer?.minimumHours,
    };

    const rowFieldMap = {
      hourlyRate: params.row?.engineerHourlyRate,
      fullDayRate: params.row?.engineerFullDayRate,
      halfDayRate: params.row?.engineerHalfDayRate,
      fthRate: params.row?.engineerFthRate,
      fixedAmount: params.row?.engineerFixedAmount,
      minimumHours: params.row?.engineerMinimumHours,
    };

    return offerFieldMap[field] ?? rowFieldMap[field] ?? null;
  };

  const handleItemClick = (value, params) => {
    if (value === "EDIT") {
      navigate(`/workOrder/edit/summary/${params?.id}`);
    }
    if (value === "DELETE") {
      handleDeleteWorkOrder(params?.id);
    }
  };

  const handleNavigate = (id) => {
    navigate(`/workOrder/details/${id}`);
  };

  //this will initially run to set the work orders data fetched from API
  useEffect(() => {
    setWorkOrders(workOrdersData?.data.workOrders);
    applyFilter();
  }, [workOrdersData]);

  //this is filter the work order based on current filter selected without API call
  useEffect(() => {
    if (!filters.status || filters.status.length === 0 || filters.status.includes("")) {
      setWorkOrders(workOrdersData?.data.workOrders);
    } else if (filters.status.includes("INFLIGHT")) {
      const filteredWorkOrders = filterInFlightWorkOrders();
      setWorkOrders(filteredWorkOrders);
    } else {
      const filteredWorkOrders = workOrdersData?.data.workOrders.filter(
        (workOrder) => !("status" in workOrder) || filters.status.includes(workOrder.status),
      );
      setWorkOrders(filteredWorkOrders);
    }
  }, [filters]);

  const filterInFlightWorkOrders = () => {
    const today = dayjs().startOf("day");
    const tomorrow = today.add(1, "day");

    const filteredWorkOrders = workOrdersData?.data.workOrders.filter((workOrder) => {
      const workOrderDate = dayjs(workOrder.orderStartDate).startOf("day");
      return workOrderDate.isSame(today, "day") || workOrderDate.isSame(tomorrow, "day");
    });

    return filteredWorkOrders;
  };

  const toggleDropdown = (id) => {
    if (openDropdownId === id) {
      setOpenDropdownId(null); // Close the dropdown if it's already open
    } else {
      setOpenDropdownId(id); // Open the dropdown for the selected row
    }
  };

  const handleDeleteWorkOrder = async (workOrderId) => {
    try {
      const payload = {
        id: workOrderId,
      };
      const response = await deleteWorkOrderMutation(payload);
      setOpenDropdownId(null); // Close the dropdown if it's already open
      dispatch(
        showToast({
          message: "Work Order deleted successfully",
          type: "success",
        }),
      );
      refetchUserWorkOrders();
      refetchStatusCounts();
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message,
          type: "error",
        }),
      );
    }
  };
  const debouncedSearch = useMemo(
    () =>
      debounce((val) => {
        dispatch(
          setClientFilters({
            module: "workOrders",
            filterName: "search",
            data: val,
          }),
        );
      }, 500),
    [dispatch],
  );
  useEffect(() => {
    setQueryParams((prev) => ({
      ...prev,
      search: search || "",
      page: 1,
    }));
  }, [search]);

  const handleSearchChange = (val) => {
    setLocalSearch(val);
    debouncedSearch(val);
  };

  const handleRemoveFilter = (key, id) => {
    if (key === "rateRange") {
      dispatch(
        setClientFilters({
          module: "workOrders",
          filterName: "minRate",
          data: "",
        }),
      );
      dispatch(
        setClientFilters({
          module: "workOrders",
          filterName: "maxRate",
          data: "",
        }),
      );
    } else if (key === "requestsRange") {
      dispatch(
        setClientFilters({
          module: "workOrders",
          filterName: "minRequests",
          data: "",
        }),
      );
      dispatch(
        setClientFilters({
          module: "workOrders",
          filterName: "maxRequests",
          data: "",
        }),
      );
    } else if (key === "dateRange") {
      dispatch(
        setClientFilters({
          module: "workOrders",
          filterName: "startDate",
          data: "",
        }),
      );
      dispatch(
        setClientFilters({
          module: "workOrders",
          filterName: "endDate",
          data: "",
        }),
      );
    } else if (key === "createDateRange") {
      dispatch(
        setClientFilters({
          module: "workOrders",
          filterName: "createStartDate",
          data: "",
        }),
      );
      dispatch(
        setClientFilters({
          module: "workOrders",
          filterName: "createEndDate",
          data: "",
        }),
      );
    } else if (["provider", "projectId", "createdById", "id", "teamId"].includes(key)) {
      if (key === "provider") {
        setSelectedEngineer(null);
      }
      dispatch(setClientFilters({ module: "workOrders", filterName: key, data: "" }));
    } else if (key === "city") {
      dispatch(
        setClientFilters({
          module: "workOrders",
          filterName: key,
          data: { value: "", label: "" },
        }),
      );
    } else if (key === "country") {
      dispatch(
        setClientFilters({
          module: "workOrders",
          filterName: key,
          data: { value: "", label: "" },
        }),
      );
      dispatch(
        setClientFilters({
          module: "workOrders",
          filterName: "city",
          data: { value: "", label: "" },
        }),
      );
    } else if (key === "clientIds") {
      dispatch(
        setClientFilters({
          module: "workOrders",
          filterName: "clientIds",
          data: clientFilters.clientIds.filter((clientId) => clientId !== id),
        }),
      );
      const selectedProject = projectsData?.data?.projects?.find(
        (p) => p.id === clientFilters.projectId,
      );

      if (selectedProject?.client?.id === id) {
        dispatch(
          setClientFilters({
            module: "workOrders",
            filterName: "projectId",
            data: null,
          }),
        );
      }
    } else if (key === "subCategoryIds") {
      dispatch(
        setClientFilters({
          module: "workOrders",
          filterName: "subCategoryIds",
          data: clientFilters.subCategoryIds.filter((subId) => subId !== id),
        }),
      );
    } else if (key === "vendorId") {
      dispatch(
        setClientFilters({
          module: "workOrders",
          filterName: "vendorId",
          data: "",
        }),
      );
      dispatch(
        setClientFilters({
          module: "workOrders",
          filterName: "subCategoryIds",
          data: [],
        }),
      );
    } else if (key === "categoryId") {
      dispatch(
        setClientFilters({
          module: "workOrders",
          filterName: "categoryId",
          data: "",
        }),
      );
      dispatch(
        setClientFilters({
          module: "workOrders",
          filterName: "vendorId",
          data: "",
        }),
      );
      dispatch(
        setClientFilters({
          module: "workOrders",
          filterName: "subCategoryIds",
          data: [],
        }),
      );
    } else if (key === "teamIds") {
      dispatch(
        setClientFilters({
          module: "workOrders",
          filterName: "teamIds",
          data: clientFilters.teamIds.filter((teamId) => teamId !== id),
        }),
      );
    } else if (key === "engineerCurrency") {
      dispatch(
        setClientFilters({
          module: "workOrders",
          filterName: "engineerCurrency",
          data: "",
        }),
      );

      setSelectedCurrency(null);
    } else if (key === "canceledBy") {
      dispatch(setClientFilters({ module: "workOrders", filterName: "canceledBy", data: "" }));
      dispatch(
        setClientFilters({ module: "workOrders", filterName: "cancellationReason", data: "" }),
      );
    } else {
      dispatch(setClientFilters({ module: "workOrders", filterName: key, data: "" }));
    }

    dispatch(
      setClientFilters({
        module: "workOrders",
        filterName: "pagination",
        data: { page: 0, pageSize: clientFilters.pagination.pageSize },
      }),
    );
    if (key === "status") {
      dispatch(
        setClientFilters({
          module: "workOrders",
          filterName: "status",
          data: clientFilters.status.filter((s) => s !== id),
        }),
      );

      if (clientFilters.status.length === 1) {
        dispatch(
          setClientFilters({
            module: "workOrders",
            filterName: "appliedFromDrawer",
            data: false,
          }),
        );
      }
    }

    setFiltersChanged(true);
  };
  /**
   * The All chip always shows the grand total, whatever tab is active — that is
   * what makes it "All". `totalCount` here is statusCountData.totalCount, which
   * the API computes without the status filter (it does honour the drawer
   * filters), so it is already the right number.
   *
   * This used to branch per status and return the selected status's own count
   * for anything outside a hardcoded list — which meant clicking Draft or
   * Submitted made the All chip echo that tab's number instead of the total.
   */
  function getAllChipCount(filtersStatus, statusCounts, totalCount) {
    return totalCount;
  }
  const isSingleStatusActive = (status) => {
    return (
      Array.isArray(filters.status) && filters.status.length === 1 && filters.status[0] === status
    );
  };

  const isStatusSetActive = (statuses) => {
    return (
      Array.isArray(filters.status) &&
      filters.status.length === statuses.length &&
      statuses.every((status) => filters.status.includes(status))
    );
  };

  const isMultipleStatusSelected = () => {
    return Array.isArray(filters.status) && filters.status.length > 1;
  };

  const isAllSelected = () => {
    return (
      !filters.status ||
      filters.status.length === 0 ||
      filters.status.includes("") ||
      (isMultipleStatusSelected() && !isStatusSetActive(ISSUE_STATUSES))
    );
  };

  return (
    <>
      {isUserWorkOrderLoading && <BaseLoader isLoading={isUserWorkOrderLoading} />}
      <BaseWrapper>
        <div className="bg-white rounded-lg px-5 py-3.5 shadow-md mt-4 sticky top-10 z-10">
          <div className="flex flex-wrap items-center gap-3">
            <div
              className="flex items-center border-r border-[#8C8C8C] pr-5 cursor-pointer"
              onClick={() => setIsFilterDrawerOpen(true)}
            >
              <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary">
                <i className="fa-light fa-filter text-white text-sm"></i>
              </span>
              <p className="text-primary ml-2 ">Add Filter</p>
            </div>

            <div className="flex-1 min-w-0 pl-3 overflow-x-auto whitespace-nowrap py-1 scrollbar">
              <div className="flex gap-2">
                {appliedFilters.map((f) => {
                  return (
                    <div
                      key={(f.key || "") + (f.value || "") + (f.id || "")}
                      className="flex-shrink-0 flex items-center bg-[#EAF1FF] text-sm font-medium rounded-md px-3 py-1"
                    >
                      <span className="text-primary">{f.prefix}:</span>&nbsp;
                      <p className="text-primary text-sm mx-1">{f.value}</p>
                      <button
                        onClick={() => handleRemoveFilter(f.key, f.id)}
                        className="ml-2 text-primary hover:text-red-500"
                        aria-label={`Remove ${f.prefix} ${f.value}`}
                      >
                        <i className="fas fa-times text-xs"></i>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="ml-auto flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={columnSettings.open}
                className="flex items-center gap-2 h-[36px] px-3 rounded-md border border-primary text-primary text-[13px] font-light hover:bg-[#EAF1FF] whitespace-nowrap"
                title="Customize columns"
              >
                <i className="fa-light fa-table-columns text-sm" />
                <span>Customize View</span>
              </button>
              <BaseTextField
                placeholder="Search by WO title or reference ID"
                onChange={(e) => {
                  handleSearchChange(e.target.value);
                }}
                value={localSearch}
                startAdornment={<i className="fa-light fa-magnifying-glass text-lg" />}
                className="w-full sm:w-[300px] lg:w-[350px]"
              />
            </div>
          </div>
        </div>

        <div
          className=" my-4
            flex
            overflow-x-auto
            scrollbar-thin
            scrollbar-thumb-gray-400
            scrollbar-track-gray-200
            [&::-webkit-scrollbar]:h-2
            [&::-webkit-scrollbar-thumb]:rounded
            [&::-webkit-scrollbar-thumb]:bg-gray-400
            [&::-webkit-scrollbar-track]:bg-gray-200
          "
        >
          <div className="mr-5">
            <FilterChip
              filter="Draft"
              count={
                search && filters.status.includes("DRAFT")
                  ? workOrdersData?.data?.totalCount
                  : statusCounts.DRAFT || 0
              }
              active={isSingleStatusActive("DRAFT")}
              onClick={() => handleFilterClick(["DRAFT"])}
            />
          </div>
          <div className="mr-5">
            <FilterChip
              filter="Submitted"
              count={
                search && filters.status.includes("PENDING_APPROVAL")
                  ? workOrdersData?.data?.totalCount
                  : statusCounts.PENDING_APPROVAL || 0
              }
              active={isSingleStatusActive("PENDING_APPROVAL")}
              onClick={() => handleFilterClick(["PENDING_APPROVAL"])}
            />
          </div>
          <div className="mr-5">
            <FilterChip
              filter="Published"
              count={
                search && filters.status.includes("PUBLISHED")
                  ? workOrdersData?.data?.totalCount
                  : statusCounts.PUBLISHED || 0
              }
              active={isSingleStatusActive("PUBLISHED")}
              onClick={() => handleFilterClick(["PUBLISHED"])}
            />
          </div>
          <div className="mr-5">
            <FilterChip
              filter="Assigned"
              count={
                search && filters.status.includes("ASSIGNED")
                  ? workOrdersData?.data?.totalCount
                  : statusCounts.ASSIGNED || 0
              }
              active={isSingleStatusActive("ASSIGNED")}
              onClick={() => handleFilterClick(["ASSIGNED"])}
            />
          </div>
          <div className="mr-5">
            <FilterChip
              filter="Inprogress"
              count={
                search && filters.status.includes("INPROGRESS")
                  ? workOrdersData?.data?.totalCount
                  : statusCounts.INPROGRESS || 0
              }
              active={isSingleStatusActive("INPROGRESS")}
              onClick={() => handleFilterClick(["INPROGRESS"])}
            />
          </div>

          <div className="mr-5">
            <FilterChip
              filter="Work Done"
              count={
                search && filters.status.includes("WORKDONE")
                  ? workOrdersData?.data?.totalCount
                  : statusCounts.WORKDONE || 0
              }
              active={isSingleStatusActive("WORKDONE")}
              onClick={() => handleFilterClick(["WORKDONE"])}
            />
          </div>
          <div className="mr-5">
            <FilterChip
              filter="Verified"
              count={
                search && filters.status.includes("VERIFIED")
                  ? workOrdersData?.data?.totalCount
                  : statusCounts?.VERIFIED || 0 || 0
              }
              active={isSingleStatusActive("VERIFIED")}
              onClick={() => handleFilterClick(["VERIFIED"])}
            />
          </div>
          <div className="mr-5">
            <FilterChip
              filter="Approved"
              count={
                search && filters.status.includes("APPROVED")
                  ? workOrdersData?.data?.totalCount
                  : statusCounts?.APPROVED || 0 || 0
              }
              active={isSingleStatusActive("APPROVED")}
              onClick={() => handleFilterClick(["APPROVED"])}
            />
          </div>
          <div className="mr-5">
            <FilterChip
              filter="Invoiced"
              count={
                search && filters.status.includes("INVOICED")
                  ? workOrdersData?.data?.totalCount
                  : statusCounts.INVOICED || 0
              }
              active={isSingleStatusActive("INVOICED")}
              onClick={() => handleFilterClick(["INVOICED"])}
            />
          </div>
          <div className="mr-5">
            <FilterChip
              filter="Paid"
              count={
                search && filters.status.includes("PAID")
                  ? workOrdersData?.data?.totalCount
                  : statusCounts.PAID || 0
              }
              active={isSingleStatusActive("PAID")}
              onClick={() => handleFilterClick(["PAID"])}
            />
          </div>
          <div className="mr-5">
            <FilterChip
              filter="Cancelled"
              count={
                search && filters.status.includes("CANCELLED")
                  ? workOrdersData?.data?.totalCount
                  : statusCounts.CANCELLED || 0
              }
              active={isSingleStatusActive("CANCELLED")}
              onClick={() => handleFilterClick(["CANCELLED"])}
            />
          </div>
          <div className="mr-5">
            <FilterChip
              filter="Issue"
              count={
                search && ISSUE_STATUSES.some((status) => filters.status.includes(status))
                  ? workOrdersData?.data?.totalCount
                  : ISSUE_STATUSES.reduce((sum, status) => sum + (statusCounts[status] || 0), 0)
              }
              active={isStatusSetActive(ISSUE_STATUSES)}
              onClick={() => handleFilterClick(ISSUE_STATUSES)}
            />
          </div>
          <FilterChip
            filter="All"
            count={getAllChipCount(filters.status, statusCounts, totalWorkOrdersCount)}
            active={isAllSelected()}
            onClick={() => handleFilterClick("")}
          />
        </div>

        <BaseDataTableV2
          className="max-h-[calc(100vh-300px)] "
          rowHeight={38}
          columns={columnSettings.orderedColumns}
          columnVisibilityModel={columnSettings.columnVisibilityModel}
          data={workOrdersData?.data?.workOrders || []}
          isLoading={false}
          disableColumnResize={false}
          onRowSelectionModelChange={(workOrderIds) => {
            setSelectedWorkOrderIds(workOrderIds);
            const rows = workOrdersData?.data?.workOrders || [];
            setSelectedWorkOrders?.(rows.filter((wo) => workOrderIds.includes(wo.id)));
          }}
          disableColumnSorting={false}
          sortingMode="server"
          sortModel={
            queryParams.sortBy && queryParams.sortOrder
              ? [{ field: "orderStartDate", sort: queryParams.sortOrder }]
              : []
          }
          onSortModelChange={handleSortModelChange}
          sortingOrder={["asc", "desc"]}
          checkboxSelection
          hideFooter={false}
          pagination
          rowCount={rowCount}
          pageSizeOptions={[10, 20, 50, 100]}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationChange}
          paginationMode="server"
          disableVirtualization
          sx={{
            "& .MuiDataGrid-columnHeader[data-field='__check__'], & .MuiDataGrid-cell[data-field='__check__']":
              {
                position: "sticky",
                left: 0,
                zIndex: 2,
              },
            "& .MuiDataGrid-columnHeader[data-field='id'], & .MuiDataGrid-cell[data-field='id']": {
              position: "sticky",
              left: 50, // width of the checkbox column
              zIndex: 2,
              borderRight: "1px solid #e0e0e0",
            },
            // Frozen header cells sit above frozen body cells; header is white.
            "& .MuiDataGrid-columnHeader[data-field='__check__'], & .MuiDataGrid-columnHeader[data-field='id']":
              {
                zIndex: 3,
                backgroundColor: "#fff",
              },
            "& .MuiDataGrid-row .MuiDataGrid-cell[data-field='__check__'], & .MuiDataGrid-row .MuiDataGrid-cell[data-field='id']":
              {
                backgroundColor: "#f6f6f6",
              },
            "& .MuiDataGrid-row:nth-of-type(even) .MuiDataGrid-cell[data-field='__check__'], & .MuiDataGrid-row:nth-of-type(even) .MuiDataGrid-cell[data-field='id']":
              {
                backgroundColor: "#fff",
              },
            "& .MuiDataGrid-row:hover .MuiDataGrid-cell[data-field='__check__'], & .MuiDataGrid-row:hover .MuiDataGrid-cell[data-field='id']":
              {
                backgroundColor: "#f1f1f1",
              },
            "& .MuiDataGrid-row.header-row .MuiDataGrid-cell[data-field='__check__'], & .MuiDataGrid-row.header-row .MuiDataGrid-cell[data-field='id']":
              {
                backgroundColor: "#EFF4FF",
              },
          }}
        />
        {columnSettings.isOpen && (
          <TableColumnSettings
            open={columnSettings.isOpen}
            onClose={columnSettings.close}
            title="Customize Columns"
            columns={columnSettings.columnSettingsList}
            visibility={columnSettings.columnVisibility}
            lockedFields={LOCKED_COLUMN_FIELDS}
            onApply={columnSettings.applySettings}
            onReset={columnSettings.resetSettings}
          />
        )}

        {isFilterDrawerOpen && (
          <WorkOrderFilters
            open={isFilterDrawerOpen}
            onClose={() => setIsFilterDrawerOpen(false)}
            fetchUserWorkOrders={fetchUserWorkOrders}
            currentStatus={filters.status}
            refetchStatusCounts={refetchStatusCounts}
            isWorkOrderLoading={isUserWorkOrderLoading}
            setFiltersChanged={setFiltersChanged}
            hasAppliedFilters={hasAppliedFilters}
            appliedFilters={appliedFilters}
            projectsData={projectsData}
            clientsData={clientsData}
            teamsData={teamsData}
            companyUsers={companyUsers}
          />
        )}

        {isGenerateInvoiceModalOpen && (
          <GenerateEngineerInvoiceModal
            open={isGenerateInvoiceModalOpen}
            close={setIsGenerateInvoiceModalOpen}
            selectedWorkOrderIds={selectedWorkOrderIds}
            engineer={selectedEngineer}
            role={UserType.FREELANCER}
            currency={selectedCurrency}
          />
        )}
        <Menu
          open={contextMenu !== null}
          onClose={handleClose}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined
          }
        >
          <MenuItem
            onClick={() => {
              handleNavigate(selectedWorkOrder.id);
              handleClose();
            }}
          >
            <p>Open</p>
          </MenuItem>
          <MenuItem
            onClick={() => {
              window.open(`/workOrder/details/${selectedWorkOrder.id}`, "_blank");
              handleClose();
            }}
          >
            <p>Open in New Tab</p>
          </MenuItem>
        </Menu>
      </BaseWrapper>
    </>
  );
};

export default WorkOrderTable;
